import { useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

import type {
  BasePatientDemographics,
  ExtendedPatient,
} from "@thalamos/aspire-common";

import {
  PatientCreateFormFields,
  patientCreateSchema,
} from "../PatientIndex/PatientCreateEdit.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { MissingDemographicsDialog } from "~/modals/MissingDemographicsDialog/MissingDemographicsDialog.js";

export type SoadCreatePatientProps = {
  selectedPatient: ExtendedPatient | null;
  setSelectedPatient: (patient: ExtendedPatient | null) => void;
  onContinue: (patientId: string) => void;
  setShowPatientDetails: (value: boolean) => void;
  setPatientEntryMode: (mode: "search-by-nhs-number" | "create" | null) => void;
  existingPatient?: ExtendedPatient;
};

export function SoadCreatePatient({
  selectedPatient,
  setSelectedPatient,
  onContinue,
  setShowPatientDetails,
  setPatientEntryMode,
  existingPatient,
}: SoadCreatePatientProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const isCreate = selectedPatient === null;
  const patientId = useMemo(() => {
    return selectedPatient?.id ?? v4();
  }, [selectedPatient]);

  const [showMissingDemographicsDialog, setShowMissingDemographicsDialog] =
    useState(selectedPatient?.restricted ?? false);

  return (
    <>
      <MissingDemographicsDialog
        open={showMissingDemographicsDialog}
        onClose={() => {
          setShowMissingDemographicsDialog(false);
        }}
        restricted={true}
      />

      <Formik<any>
        validationSchema={patientCreateSchema}
        initialValues={{
          givenName: selectedPatient?.name.given ?? null,
          familyNameAndAddress: {
            name: selectedPatient?.name.family ?? null,
            postalCode: selectedPatient?.address.postalCode ?? null,
            address: selectedPatient?.address.address ?? null,
            isConfirmed: selectedPatient?.address.address ? true : false,
          },
          isPatientDateOfBirth: selectedPatient?.dateOfBirth ? true : false,
          dateOfBirth: selectedPatient?.dateOfBirth ?? null,
          deathNotificationStatus:
            existingPatient?.deathNotificationStatus ?? null,
        }}
        onSubmit={async (values) => {
          const trimmedDemographics: BasePatientDemographics = {
            dateOfBirth: values.dateOfBirth,
            name: {
              family: values.familyNameAndAddress.name.trim(),
              given: values.givenName.trim(),
            },
            address: {
              address: values.familyNameAndAddress.address.trim(),
              postalCode: values.familyNameAndAddress.postalCode
                ?.trim()
                ?.toUpperCase(),
            },
          };

          if (isCreate) {
            const response = await api.patients.create(patientId, {
              type: "demographics",
              demographics: trimmedDemographics,
            });

            if (response.status === 200) {
              onContinue(patientId);
            } else {
              renderErrorToast({ message: "Failed to create patient" });
            }
          } else {
            if (
              selectedPatient.name.family !== trimmedDemographics.name.family ||
              selectedPatient.name.given !== trimmedDemographics.name.given ||
              selectedPatient.address.address !==
                trimmedDemographics.address.address ||
              selectedPatient.address.postalCode !==
                trimmedDemographics.address.postalCode ||
              selectedPatient.dateOfBirth !== trimmedDemographics.dateOfBirth
            ) {
              const updateResult = await api.patients.update(patientId, {
                type: "demographics",
                demographics: trimmedDemographics,
                expectedVersion: selectedPatient.version,
                deathNotificationStatus:
                  selectedPatient.deathNotificationStatus,
                deceasedDateTime: selectedPatient.deceasedDateTime,
              });

              if (updateResult.status !== 200) {
                renderErrorToast({
                  message: "Patient update failed.. continuing...",
                });
              }
            }

            onContinue(patientId);
          }
        }}
      >
        {({
          values,
          setValues,
          errors,
          touched,
          setFieldTouched,
          handleBlur,
          setValues: formikSetValues,
          submitForm,
          isSubmitting,
        }) => {
          const fieldProps = {
            validationSchema: patientCreateSchema,
            context: {},
            values,
            errors,
            handleBlur,
            setFieldTouched,
            touched,
            setValues,
          };
          return (
            <>
              {selectedPatient?.restricted && (
                <Banner
                  sx={{ mb: 2 }}
                  bannerType={BannerList.ERROR}
                  title="Patient is marked as restricted in NHS National Systems. Address details are sensitive and not synchronised to/from other systems"
                />
              )}
              <Banner
                bannerType={BannerList.INFO}
                body={t("pages.soadAssessment.patientCreate.info")}
                sx={{ mb: 3 }}
              />
              <PatientCreateFormFields
                fieldProps={fieldProps}
                values={values}
                setValues={formikSetValues}
                nhsNumber={selectedPatient?.nhsNumber ?? null}
              />
              <FormFooterSection
                customFooterBackgroundColor={theme.palette.common.white}
                onSave={submitForm}
                discardLabel={t("buttonLabels.goBack")}
                saveLabel={t("buttonLabels.continue")}
                saveVariant="contained"
                onCancel={() => {
                  setSelectedPatient(null);
                  setPatientEntryMode(null);
                  setShowPatientDetails(false);
                }}
                disableDiscard={isSubmitting}
                disableSubmit={isSubmitting}
                minHeight
              />
            </>
          );
        }}
      </Formik>
    </>
  );
}
